import type { App, Plugin } from 'vue';
import { SignedUrlCache } from '@/plugins/signedUrl/cache.ts';
import { cacheKey, SignedUrlKind, SignedUrlResolvers } from '@/plugins/signedUrl/resolvers.ts';

export interface SignedUrlApi {
    /**
     * Resolve a signed URL for the given kind. Re-uses an in-memory
     * cache and deduplicates concurrent requests. Use this when you
     * need the raw URL (e.g. to set window.location).
     */
    resolve<K extends SignedUrlKind>(
        kind: K,
        ...params: Parameters<(typeof SignedUrlResolvers)[K]>
    ): Promise<string>;

    /**
     * Drop the cached entry for a (kind, params) tuple so the next
     * resolve fetches a fresh URL. Useful after a 403/expiry race.
     */
    invalidate<K extends SignedUrlKind>(
        kind: K,
        ...params: Parameters<(typeof SignedUrlResolvers)[K]>
    ): void;
}

const cache = new SignedUrlCache();

export const SignedUrl: SignedUrlApi = {
    resolve(kind, ...params) {
        const key = cacheKey(kind, params as ReadonlyArray<unknown>);
        const resolver = SignedUrlResolvers[kind] as (...args: unknown[]) => Promise<string>;
        return cache.resolve(key, () => resolver(...(params as unknown[])));
    },

    invalidate(kind, ...params) {
        const key = cacheKey(kind, params as ReadonlyArray<unknown>);
        cache.invalidate(key);
    },
};

declare module '@vue/runtime-core' {
    interface ComponentCustomProperties {
        $signedUrl: SignedUrlApi;
    }
}

/**
 * Vue 3 plugin. Registers `$signedUrl` on `app.config.globalProperties`
 * for Options-API consumers (`this.$signedUrl`) and provides the same
 * instance via `app.provide('$signedUrl', ...)` for Composition-API
 * consumers (`inject('$signedUrl')`).
 */
export const SignedUrlPlugin: Plugin = {
    install(app: App) {
        app.config.globalProperties.$signedUrl = SignedUrl;
        app.provide('$signedUrl', SignedUrl);
    },
};

// Test hook — never use in production code.
export function __resetSignedUrlCacheForTests(): void {
    cache.clear();
}

export { SignedUrlCache, cacheKey, SignedUrlResolvers };
export type { SignedUrlKind };
